import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiService from '../services/apiService';
import type { Capability } from '../config/capabilities';
import type { FeatureKey, FeatureMap } from '../config/features';
import { ALL_FEATURES_ON } from '../config/features';

interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  language: string;
  isActive: boolean;
  role: 'admin' | 'bearbeiter';
  // Feingranulare Bereichsrechte (nur relevant für Bearbeiter; Admins haben alles).
  permissions?: Capability[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  loginWithSession: (token: string, user: User) => void;
  logout: () => void;
  loading: boolean;
  isLoading: boolean; // Add alias for compatibility
  // Prüft, ob der aktuelle Benutzer Zugriff auf einen Bereich hat.
  // Admins immer true; Bearbeiter nur bei vorhandener Capability.
  can: (capability: Capability) => boolean;
  // Vendor-Feature-Flags (Paket-Steuerung): ob die Praxis den Bereich gebucht hat.
  hasFeature: (feature: FeatureKey) => boolean;
  // Aktualisiert die lokale Feature-Map (nach PUT /api/features im Vendor-Tab).
  setFeatures: (features: FeatureMap) => void;
  // Darf der eingeloggte Benutzer Features togglen (VENDOR_USERS, Anbieter-Seite)?
  isVendor: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  // Bis /api/features geantwortet hat: alles an (kein Flackern, Bestandsverhalten).
  const [features, setFeatures] = useState<FeatureMap>(ALL_FEATURES_ON);
  const [isVendor, setIsVendor] = useState<boolean>(false);

  // Feature-Flags nach Login laden. Fehler → Defaults (alles an) behalten:
  // ein kaputter Flags-Endpoint darf die Praxis nie aussperren.
  useEffect(() => {
    if (!isAuthenticated) {
      setFeatures(ALL_FEATURES_ON);
      setIsVendor(false);
      return;
    }
    let mounted = true;
    apiService.getFeatures()
      .then(resp => {
        if (mounted && resp.data?.success && resp.data.data) {
          setFeatures({ ...ALL_FEATURES_ON, ...resp.data.data.features });
          setIsVendor(!!resp.data.data.is_vendor);
        }
      })
      .catch(() => { /* Defaults behalten */ });
    return () => { mounted = false; };
  }, [isAuthenticated]);

  useEffect(() => {
    // Check for existing authentication on mount
    const checkAuthStatus = () => {
      try {
        const storedToken = localStorage.getItem('admin_token');
        const storedUser = localStorage.getItem('admin_user');
        
        if (storedToken && storedUser) {
          // Validate token format (basic check)
          if (storedToken.split('.').length === 3) {
            // JWT token format validation
            try {
              const payload = JSON.parse(atob(storedToken.split('.')[1]));
              const currentTime = Date.now() / 1000;
              
              if (payload.exp && payload.exp > currentTime) {
                // Token is valid and not expired
                const userData = JSON.parse(storedUser);
                
                // Validate user data structure (check for required new fields)
                if (userData.id && userData.username && userData.email && userData.firstName && userData.lastName && userData.role) {
                  setToken(storedToken);
                  setUser(userData);
                  setIsAuthenticated(true);
                } else {
                  // Old user data format, clear it
                  localStorage.removeItem('admin_token');
                  localStorage.removeItem('admin_user');
                }
              } else {
                // Token expired, remove it
                localStorage.removeItem('admin_token');
                localStorage.removeItem('admin_user');
              }
            } catch (error) {
              // Invalid token format, remove it
              localStorage.removeItem('admin_token');
              localStorage.removeItem('admin_user');
            }
          }
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await apiService.login({ username, password });
      
      if (response.data.success && response.data.token) {
        const { token: newToken, user: userData } = response.data;
        
        setToken(newToken);
        setUser(userData);
        setIsAuthenticated(true);
        
        // Store in localStorage
        localStorage.setItem('admin_token', newToken);
        localStorage.setItem('admin_user', JSON.stringify(userData));
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const loginWithSession = (newToken: string, userData: User) => {
    setToken(newToken);
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('admin_token', newToken);
    localStorage.setItem('admin_user', JSON.stringify(userData));
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    apiService.logout();
  };

  const can = (capability: Capability): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return Array.isArray(user.permissions) && user.permissions.includes(capability);
  };

  const hasFeature = (feature: FeatureKey): boolean => features[feature] !== false;

  const value: AuthContextType = {
    isAuthenticated,
    user,
    token,
    login,
    loginWithSession,
    logout,
    loading,
    isLoading: loading, // Add alias for compatibility
    can,
    hasFeature,
    setFeatures,
    isVendor,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 